import React from 'react';
import { useTranslation } from 'react-i18next';
import productsImg from '../Products/img/products-inspection-content.webp';
import './css/products.css';

const IMAGE_SRC = productsImg;

// 正規化語言（測試機若為 zh-TW / zh-CN 轉為 zh）
const normalizeLang = (lng) => {
  if (!lng) return 'en';
  const lower = lng.toLowerCase();
  if (lower.startsWith('zh')) return 'zh';
  return lower;
};

// 同步確保資源存在，避免首次 render 取不到翻譯
const ensureInspectionI18n = (i18n) => {
  const ns = 'inspection';
  const norm = normalizeLang(i18n.language);
  if (norm !== i18n.language) {
    i18n.changeLanguage(norm);
  }
  if (!i18n.options.fallbackLng) {
    i18n.options.fallbackLng = ['zh', 'en'];
  }
  if (!i18n.hasResourceBundle('en', ns)) {
    i18n.addResourceBundle('en', ns, {
      sectionTitle: 'Surface Inspection System',
      sectionSubtitle: 'High-Performance, Expandable, Cost-Effective',
      title: 'WINTRISS WEB RANGER®',
      tagline: 'High-Performance, Expandable, Cost-Effective',
      intro: 'The Web Ranger® is an optical surface inspection and defect classification system that provides a total quality solution for products produced in continuous webs.',
      body1: 'The system is comprised of Smart Cameras, Patented Lighting, and Defect Detection Software.',
      body2: 'The highly modular design allows the system to be tailored to any application based on web width, web speed, and defect size (resolution) requirements.',
      body3: 'The incorporation of smart cameras with embedded processing resources enables the system to provide 100% inspection of the material and rapid, accurate detection of defects.',
      architectureTitle: 'MODULAR ARCHITECTURE',
      viewFull: 'View fullsize',
      featuresTitle: 'Key Capabilities',
      performanceDesc: 'Delivers rapid, accurate defect detection at full production web speeds.',
      expandableDesc: 'Modular architecture scales with web width, speed, and resolution needs.',
      costEffectiveDesc: 'Optimized smart hardware lowers total cost of ownership.',
      modularDesc: 'Flexible plug-and-play components enable tailored configurations.',
      smartCamerasDesc: 'On-camera processing provides real-time defect classification.',
      fullInspectionDesc: 'Ensures 100% continuous coverage so defects are not missed.'
    });
  }
  if (!i18n.hasResourceBundle('zh', ns)) {
    i18n.addResourceBundle('zh', ns, {
      sectionTitle: '表面檢測系統',
      sectionSubtitle: '高效能・可擴充・高性價比',
      title: 'WINTRISS WEB RANGER®',
      tagline: '高效能・可擴充・高性價比',
      intro: 'Web Ranger® 是一套針對連續捲料產品的光學表面檢測與缺陷分類系統，提供整體品質解決方案。',
      body1: '系統由智慧相機、專利光源與缺陷檢測軟體組成。',
      body2: '高度模組化設計可依捲料寬度、速度與缺陷尺寸（解析度）需求量身配置。',
      body3: '內建處理資源的智慧相機可達成 100% 全檢並快速且精準地偵測缺陷。',
      architectureTitle: '模組化架構',
      viewFull: '查看原尺寸',
      featuresTitle: '核心能力',
      performanceDesc: '在完整生產線速度下仍能快速且精準地偵測缺陷。',
      expandableDesc: '模組化架構可依寬度、速度與解析度需求彈性擴充。',
      costEffectiveDesc: '智慧硬體與最佳化配置降低整體擁有與維護成本。',
      modularDesc: '彈性元件可插拔組合，滿足多樣化場域需求。',
      smartCamerasDesc: '相機端即時處理與分類缺陷，縮短回饋時間。',
      fullInspectionDesc: '達成 100% 連續覆蓋，不遺漏任何缺陷。'
    });
  }
};

const Inspection = () => {
  const { t, i18n } = useTranslation();
  ensureInspectionI18n(i18n); // 同步呼叫，避免 useEffect 首渲染空白
  const lang = normalizeLang(i18n.language);

  const featureItems = [
    { k: 'performance', zh: '高效能', en: 'High Performance', descKey: 'performanceDesc' },
    { k: 'expandable', zh: '可擴充性', en: 'Expandable', descKey: 'expandableDesc' },
    { k: 'costEffective', zh: '高性價比', en: 'Cost-Effective', descKey: 'costEffectiveDesc' },
    { k: 'modular', zh: '模組化', en: 'Modular', descKey: 'modularDesc' },
    { k: 'smartCameras', zh: '智慧相機', en: 'Smart Cameras', descKey: 'smartCamerasDesc' },
    { k: 'fullInspection', zh: '100% 全檢', en: '100% Inspection', descKey: 'fullInspectionDesc' }
  ];

  return (
    <div>
      <section className="inspection-section relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('inspection:sectionTitle')}</h1>
          <p className="text-xl text-white">{t('inspection:sectionSubtitle')}</p>
        </div>
      </section>

      <main
        style={{
          padding: 'clamp(1rem, 2vw, 2.5rem)',
          maxWidth: '1200px',
          margin: '0 auto',
          fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
          lineHeight: 1.6
        }}
      >
        <header style={{ marginBottom: '1.5rem' }}>
          <h1 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
            margin: 0,
            letterSpacing: '.5px'
          }}>
            {t('inspection:title')}
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2.2vw, 1.25rem)',
            color: '#555',
            margin: '.5rem 0 0'
          }}>
            {t('inspection:tagline')}
          </p>
        </header>

        <section
          style={{
            display: 'grid',
            gap: '2rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
            alignItems: 'start',
            marginBottom: '2.5rem'
          }}
        >
          <article>
            <p>{t('inspection:intro')}</p>
            <p>{t('inspection:body1')}</p>
            <p>{t('inspection:body2')}</p>
            <p>{t('inspection:body3')}</p>
          </article>
          <figure
            style={{
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '.75rem'
            }}
          >
            <div
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,.12)'
              }}
            >
              <img
                src={IMAGE_SRC}
                alt={t('inspection:architectureTitle')}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover'
                }}
                loading="lazy"
              />
            </div>
          </figure>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.4rem',
            margin: '0 0 1.25rem',
            fontWeight: 600,
            letterSpacing: '.5px'
          }}>
            {t('inspection:featuresTitle')}
          </h2>
          <div
            style={{
              display: 'grid',
              gap: '1.25rem',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))'
            }}
            aria-label="Key attributes"
          >
            {featureItems.map(item => (
              <div
                key={item.k}
                style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  border: '1px solid #e2e6ea',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '.5rem'
                }}
              >
                <strong style={{ fontSize: '.95rem' }}>
                  {lang === 'zh' ? item.zh : item.en}
                </strong>
                <p style={{ fontSize: '.75rem', color: '#555', margin: 0 }}>
                  {t(`inspection:${item.descKey}`)}
                </p>
                <span style={{
                  fontSize: '.65rem',
                  letterSpacing: '.5px',
                  color: '#888',
                  textTransform: 'uppercase'
                }}>
                  {item.k}
                </span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Inspection;

